import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AnimatedBackground } from '@/components/ui/animated-background';
import { Shield, MapPin, Database, Users, FileText, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function TrustPage() {
  const trustSignals = [
    {
      icon: MapPin,
      title: 'UK and EU Data Residency',
      description: 'AI analysis in the EU; app and data in London (UK)',
      details:
        'The ASI scoring pipeline runs on Google Cloud in London (europe-west2), where your account data and analysis results are stored. AI analysis uses Google Vertex AI through its EU endpoint, so statements are processed within EU member states. All processing occurs under GDPR-compliant terms.',
    },
    {
      icon: Database,
      title: 'No AI Training',
      description: 'Your statements are never used to train models',
      details:
        'Your PDF is processed only for your analysis, then discarded. It is not retained by Google or used to train AI models. We keep the analysis results (scores, advice and short AI-written assessment notes that include excerpts from the statement) under a SHA-256 fingerprint, so the same PDF keeps its scores when assessed again with the same framework and model version. Raw PDFs are never stored.',
    },
    {
      icon: Shield,
      title: 'GDPR Compliant',
      description: 'EU data protection by design',
      details:
        'Our infrastructure and processes are designed with GDPR compliance at their core. All sub-processors operate under EU data-protection agreements. We document all six GDPR rights (access, rectification, erasure, portability, objection, and restriction) in our privacy policy.',
    },
    {
      icon: Users,
      title: 'Transparent Sub-processors',
      description: 'See our providers and roles',
      details:
        'We maintain a public register of all infrastructure and service providers that process data on your behalf. Every sub-processor is listed with their role, data processing regions, and compliance measures.',
    },
  ];

  return (
    <AnimatedBackground className="w-full">
      <div className="mx-auto max-w-5xl px-6 py-16">
        <div className="mb-12 space-y-6">
          <div className="flex items-center gap-3">
            <Shield className="h-10 w-10 text-[var(--accent)]" />
            <h1 className="font-display text-4xl font-bold !tracking-display text-[var(--text-primary)]">
              Enterprise Trust & Security
            </h1>
          </div>
          <p className="text-xl leading-relaxed text-[var(--text-secondary)]">
            Built for privacy-conscious organizations
          </p>
        </div>

        <div className="space-y-6">
          {trustSignals.map((signal, index) => (
            <Card key={index} className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-2xl">
                  <div className="rounded-xl bg-[var(--accent)]/10 p-3">
                    <signal.icon className="h-6 w-6 text-[var(--accent)]" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xl text-success-700">✓</span>
                      <span>{signal.title}</span>
                    </div>
                    <p className="mt-1 font-sans text-sm font-normal tracking-normal text-[var(--text-secondary)]">{signal.description}</p>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[var(--text-secondary)] leading-relaxed">{signal.details}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="mt-12 space-y-6">
          <Card className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Lock className="h-6 w-6 text-[var(--accent)]" />
                Additional Security Measures
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4">
                  <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Encryption at Rest</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    All stored data is encrypted using Google Cloud's encryption standards with centralized key management.
                  </p>
                </div>
                <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4">
                  <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Encryption in Transit</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    All data in transit is encrypted using TLS 1.3+ with modern cipher suites.
                  </p>
                </div>
                <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4">
                  <h3 className="mb-2 font-semibold text-[var(--text-primary)]">Access Controls</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    Role-based access control ensures only authorized users can access analysis results.
                  </p>
                </div>
                <div className="rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4">
                  <h3 className="mb-2 font-semibold text-[var(--text-primary)]">PDF Validation</h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    All uploads are validated for size limits (20MB) and file integrity before processing.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-[rgba(31,76,84,0.18)] bg-white/80 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-2xl">
                <FileText className="h-6 w-6 text-[var(--accent)]" />
                Documentation & Transparency
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-[var(--text-secondary)]">
                  We believe in full transparency about how we handle your data. Explore our comprehensive documentation:
                </p>
                <div className="flex flex-col gap-3">
                  <Link
                    to="/privacy"
                    className="flex items-center justify-between rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4 transition hover:border-[var(--accent)] hover:bg-[var(--accent-light)]/50"
                  >
                    <span className="font-semibold text-[var(--text-primary)]">Privacy Policy</span>
                    <span className="text-[var(--accent)]">→</span>
                  </Link>
                  <Link
                    to="/sub-processors"
                    className="flex items-center justify-between rounded-xl border border-[rgba(31,76,84,0.12)] bg-[var(--accent-light)]/30 p-4 transition hover:border-[var(--accent)] hover:bg-[var(--accent-light)]/50"
                  >
                    <span className="font-semibold text-[var(--text-primary)]">Sub-processor Register</span>
                    <span className="text-[var(--accent)]">→</span>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="mt-12 rounded-xl border border-[rgba(31,76,84,0.18)] bg-gradient-to-br from-[var(--accent-light)]/40 to-white/60 p-8 text-center">
          <h2 className="mb-3 font-display text-2xl font-semibold !tracking-display text-[var(--text-primary)]">Questions About Our Security?</h2>
          <p className="mb-6 text-[var(--text-secondary)]">
            Our team is here to help with compliance documentation, data processing agreements, or technical security questions.
          </p>
          <a
            href="mailto:privacy@antislaveryintelligence.co.uk"
            className="inline-flex items-center gap-2 rounded-xl bg-[var(--accent)] px-6 py-3 font-semibold text-white transition hover:opacity-90"
          >
            Contact Privacy Team
          </a>
        </div>
      </div>
    </AnimatedBackground>
  );
}
